import React, { useCallback, useState } from "react";
import { BinaryBlobResponse, Release } from "../../models";
import { HTTP_CLIENT } from "../../hooks";
import { toast } from "react-toastify";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbTack } from "@fortawesome/free-solid-svg-icons";
import { ReleaseNotesButton } from "../ReleaseNotesModal";
import { Button } from "react-bootstrap";
import { Tr3FirmwareFileList } from "./Tr3FirmwareFileList";
import { Tr3BootloaderFileList } from "./Tr3BootloaderFileList";
import { Tr3SeFidoAppletFileList } from "./Tr3SeFidoAppletFileList";
import { Tr3SePivAppletFileList } from "./Tr3SePivAppletFileList";
import { BundleFileList } from "./BundleFileList";
import { Tr2FileList } from "./Tr2FileList";
import { ReleaseFileList } from "./ReleaseFileList";
import { DATA_TYPE_BUNDLE_MANIFEST } from "../../utils";

const BINARY_BLOBS_PATH_PREFIX = "/api/v2/binaryBlobs";

interface Props {
  release: Release;
  model: number;
  jwt: string | null;
  editReleaseNotesClicked: () => void;
}

function fileList(
  model: number,
  release: Release,
  releaseFiles: ReleaseFileList,
) {
  switch (model) {
    case 1:
      return <Tr2FileList releaseFiles={releaseFiles} release={release} />;
    case 2:
      return <Tr3FirmwareFileList releaseFiles={releaseFiles} release={release} />;
    case 3:
      return (
        <Tr3BootloaderFileList releaseFiles={releaseFiles} release={release} />
      );
    case 4:
      return (
        <Tr3SeFidoAppletFileList releaseFiles={releaseFiles} release={release} />
      );
    case 5:
      return (
        <Tr3SePivAppletFileList releaseFiles={releaseFiles} release={release} />
      );
    case 6:
      return (
        <BundleFileList releaseFiles={releaseFiles} release={release} />
      );
    default:
      return (
        <>
          Model {model} is unsupported. Go away, I'm busy with important things.
        </>
      );
  }
}

export function ReleaseTableRow({
  release,
  model,
  jwt,
  editReleaseNotesClicked,
}: Props) {
  const [releaseLoadTriggered, setReleaseLoadTriggered] = useState(false);
  const [releaseFiles, setReleaseFiles] = useState<ReleaseFileList>({});

  const updateBlobsList = useCallback(async () => {
    const blobsResponse = await HTTP_CLIENT.get<BinaryBlobResponse>({
      path: `${BINARY_BLOBS_PATH_PREFIX}/search/${jwt ? "findByRelease" : "findAnonymouslyByRelease"}`,
      query: { release: release.id },
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    const files = blobsResponse._embedded.binaryBlobs;
    files.forEach((file) => {
      console.log(`Got binary blob with file type ${file.dataTypeRaw}`);
    });
    setReleaseFiles({
      releaseAppOnly: files.find((b) => b.dataTypeRaw === 1),
      releaseAppAndBootloaderDevKeys: files.find((b) => b.dataTypeRaw === 2),
      releaseFimeTest: files.find((b) => b.dataTypeRaw === 3),
      bootloaderRelease: files.find((b) => b.dataTypeRaw === 4),
      debugAppOnly: files.find((b) => b.dataTypeRaw === 5),
      debugAppAndBootloader: files.find((b) => b.dataTypeRaw === 6),
      debugFimeTest: files.find((b) => b.dataTypeRaw === 7),
      bootloaderDebug: files.find((b) => b.dataTypeRaw === 8),
      locationsFile: files.find((b) => b.dataTypeRaw === 9),
      translationsFile: files.find((b) => b.dataTypeRaw === 10),
      releaseAppOnlyElf: files.find((b) => b.dataTypeRaw === 11),
      releaseMfgElf: files.find((b) => b.dataTypeRaw === 12),
      bootloaderReleaseElf: files.find((b) => b.dataTypeRaw === 13),
      releaseMfg: files.find((b) => b.dataTypeRaw === 14),
      releaseAppOnlyEncryptedDevKeys: files.find((b) => b.dataTypeRaw === 15),
      releaseMfgEncryptedDevKeys: files.find((b) => b.dataTypeRaw === 16),
      releaseAppOnlyEncryptedProdKeys: files.find((b) => b.dataTypeRaw === 17),
      releaseMfgEncryptedProdKeys: files.find((b) => b.dataTypeRaw === 18),
      releaseRebootstrapper: files.find((b) => b.dataTypeRaw === 19),
      releaseRebootstrapperElf: files.find((b) => b.dataTypeRaw === 20),
      releaseAppAndBootloaderProdKeys: files.find((b) => b.dataTypeRaw === 21),
      releaseRebootstrapperSfbDevKeys: files.find((b) => b.dataTypeRaw === 22),
      releaseRebootstrapperSfbProdKeys: files.find((b) => b.dataTypeRaw === 23),
      releaseRebootstrapperBundleDevKeys: files.find((b) => b.dataTypeRaw === 24),
      sePackageJwtProdKeys: files.find((b) => b.dataTypeRaw === 25 || b.dataTypeRaw === 27),
      sePackageJwtDevKeys: files.find((b) => b.dataTypeRaw === 26 || b.dataTypeRaw === 28),
      bundleJson: files.find((b) => b.dataTypeRaw === DATA_TYPE_BUNDLE_MANIFEST),
      seLogicJwtProdKeys: files.find((b) => b.dataTypeRaw === 30 || b.dataTypeRaw === 32),
      seLogicJwtDevKeys: files.find((b) => b.dataTypeRaw === 31 || b.dataTypeRaw === 33),
    });
  }, [release, jwt]);

  return (
    <tr>
      <td>
        <FontAwesomeIcon
          icon={faThumbTack}
          style={{
            marginRight: "0.8em",
            opacity: release.pinned ? 1 : 0,
          }}
        />
        <code>{release.version}</code>
      </td>
      {releaseLoadTriggered ? (
        fileList(model, release, releaseFiles)
      ) : (
        <td colSpan={3}>
          <Button
            size="sm"
            onClick={async () => {
              setReleaseLoadTriggered(true);
              try {
                await updateBlobsList();
              } catch (e: any) {
                toast.error(e.message);
              }
            }}
          >
            Load Files
          </Button>
        </td>
      )}
      <td>
        <ReleaseNotesButton
          jwt={jwt}
          releaseNotes={release.releaseNotes}
          onClick={editReleaseNotesClicked}
        />
      </td>
      <td>{release.buildAuthor}</td>
      <td>
        Uploaded: {release.uploadTime}
        <br />
        Compiled: {release.buildTime}
      </td>
      <td>
        <a href={release.buildUrl} target="_blank" rel="noreferrer">
          {release.vcsCommit.substring(0, 7)}
        </a>
      </td>
    </tr>
  );
}
